import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { NotificationEvent, SyncEventSource } from '../sync-event.js';
import { OrderReview } from './review/order-review.js';

export class ReviewCreatedEvent extends NotificationEvent {
  static readonly TYPE_ID: string = '0ec4f8a7df8041f5b28c2c3a6f8ea400';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly review: OrderReview,
    public readonly seen: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return ReviewCreatedEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ReviewCreatedEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ReviewCreatedEvent {
    return new ReviewCreatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creationId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        context.fromData(MapUtils.get<SerializedData>(map, 'review')),
        MapUtils.get<boolean>(map, 'seen'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creationId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'review': context.toData(this.review),
      'seen': this.seen,
    };
  }

  clone(): this {
    return new ReviewCreatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.review.clone(),
        this.seen,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        isSameData(other.review, this.review) &&
        other.seen === this.seen;
  }

  markAsSeen(): this {
    return new ReviewCreatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.review.clone(),
        true,
    ) as this;
  }
}

export class ReviewCreatedEventBuilder extends DataModelBuilder<ReviewCreatedEvent> {
  build(context: SerializationContext, map: SerializedData): ReviewCreatedEvent {
    return ReviewCreatedEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ReviewCreatedEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ReviewCreatedEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
